/**
 * GoodsReceiptScanPage (Jul-2026, User-Wunsch) — mobiler Wareneingang MIT
 * LOGIN-PFLICHT, per QR aus der Warenwirtschaft gestartet.
 * Ablauf: Anmelden (falls nötig) → Lieferschein fotografieren/PDF → KI erkennt
 * Positionen (detaillierte Anzeige wie am PC) → Positionen prüfen → direkt am
 * Handy ins Lager buchen (nutzt die bestehende Lieferschein-Scan-/Buchungslogik).
 * Route: /wareneingang-scan/:token
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ScanLine, Check, AlertCircle, Camera, Sparkles, Package, RotateCcw, UserCheck } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const DOC_TYPE_DE = { delivery_note: "Lieferschein", invoice: "Rechnung", combined: "Lieferschein + Rechnung", control_only: "Nur Rechnungskontrolle" };
const DOC_TYPES = ["delivery_note", "invoice", "combined", "control_only"];

export default function GoodsReceiptScanPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [scan, setScan] = useState(null);
    const [rows, setRows] = useState([]);
    const [supplierName, setSupplierName] = useState("");
    const [dnNumber, setDnNumber] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [booked, setBooked] = useState(0);
    const [commissionMode, setCommissionMode] = useState(false);
    const [commissionTarget, setCommissionTarget] = useState(null);
    const [decisionRequired, setDecisionRequired] = useState(false);
    const [decisionMade, setDecisionMade] = useState(false);
    const [scanError, setScanError] = useState("");
    const [docType, setDocType] = useState("delivery_note");
    const [dupWarn, setDupWarn] = useState(null);
    const fileRef = useRef(null);

    const loadInfo = useCallback(async () => {
        try {
            const { data } = await api.get(`/public/goods-receipt-scan/${token}`);
            setInfo(data); setBooked(data.booked_count || 0);
        } catch (e) {
            // Login-Pflicht (User-Wunsch): ohne Anmeldung → zur Login-Seite und zurück.
            if (e.response?.status === 401) {
                window.location.assign(`/login?next=${encodeURIComponent(`/wareneingang-scan/${token}`)}`);
                return;
            }
            setError(e.response?.data?.detail || "Wareneingang-Sitzung ungültig oder abgelaufen.");
        } finally { setLoading(false); }
    }, [token]);
    useEffect(() => { loadInfo(); }, [loadInfo]);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { setScanError("Datei zu groß (max 10 MB). Bitte Foto in geringerer Auflösung aufnehmen."); return; }
        setScanning(true);
        setResult(null);
        setScanError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/public/goods-receipt-scan/${token}/scan`, fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
            });
            if (data.mocked || (data.items || []).length === 0) {
                // KI-Fehler / nichts erkannt → NICHT stumm bleiben, sondern erklären.
                setScanError(
                    data.mocked
                        ? `Die KI konnte das Dokument nicht auswerten (${data.reason || "Service nicht erreichbar"}). Bitte erneut fotografieren — Blatt glatt, gutes Licht, ganze Seite im Bild.`
                        : "Die KI hat keine Positionen erkannt. Bitte erneut fotografieren — Blatt glatt, gutes Licht, ganze Seite im Bild."
                );
                setScanning(false);
                return;
            }
            setScan(data);
            setSupplierName(data.supplier_name || "");
            setDnNumber(data.delivery_note_number || "");
            setDocType(DOC_TYPES.includes(data.doc_type) ? data.doc_type : "delivery_note");
            setDupWarn(null);
            setCommissionMode(!!data.is_commission);
            setCommissionTarget((data.commission_suggestions || [])[0] || null);
            const need = !!data.commission_decision_required;
            setDecisionRequired(need);
            setDecisionMade(!need);
            const initRows = (data.items || []).map((m) => ({
                idx: m.idx,
                action: m.match ? "match" : "create",
                article_id: m.match?.article_id || null,
                sku: m.match?.sku || m.scanned?.sku || "",
                name: m.scanned?.name || "",
                quantity: m.scanned?.quantity || 0,
                unit: m.scanned?.unit || "Stück",
                purchase_price_net: m.scanned?.unit_price_net || m.match?.purchase_price_net || 0,
                _matchInfo: m.match,
            }));
            setRows(initRows);
            if (data.mocked) toast.warning(`Erkennung ohne KI (${data.reason || "Service nicht erreichbar"}). Bitte Positionen prüfen.`);
            else toast.success(`${initRows.length} Position(en) erkannt`);
        } catch (err) {
            if (err.response?.status === 401) {
                window.location.assign(`/login?next=${encodeURIComponent(`/wareneingang-scan/${token}`)}`);
                return;
            }
            setScanError(
                err.code === "ECONNABORTED"
                    ? "Zeitüberschreitung bei der KI-Analyse. Bitte erneut versuchen."
                    : (err.response?.data?.detail || "Scan fehlgeschlagen. Bitte erneut versuchen.")
            );
        }
        setScanning(false);
    };

    const updateRow = (idx, patch) => setRows((rs) => rs.map((r) => (r.idx === idx ? { ...r, ...patch } : r)));

    const commit = async (force = false) => {
        const controlOnly = docType === "control_only";
        const items = controlOnly ? [] : rows.filter((r) => r.action !== "skip" && Number(r.quantity) > 0).map((r) => ({
            action: r.action, article_id: r.article_id, sku: r.sku, name: r.name,
            quantity: Number(r.quantity), unit: r.unit,
            purchase_price_net: Number(r.purchase_price_net) || null,
        }));
        if (!controlOnly && items.length === 0) { toast.error("Keine Positionen zum Buchen"); return; }
        if (decisionRequired && !decisionMade) { toast.error("Bitte wählen: Kommission oder eigenes Lager"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/public/goods-receipt-scan/${token}/commit`, {
                supplier_name: supplierName, delivery_note_number: dnNumber, items,
                doc_type: docType,
                invoice_number: scan?.invoice_number || null,
                total_gross: scan?.total_gross || null,
                recipient_name: scan?.recipient_name || null,
                is_commission: commissionMode,
                consignment_target: commissionMode ? commissionTarget : null,
                fingerprint: scan?.fingerprint, file_url: scan?.file_url || null,
                file_storage_path: scan?.file_storage_path || null, force,
            });
            setResult(data);
            setBooked((b) => b + 1);
            setDupWarn(null);
            toast.success(controlOnly
                ? "Beleg zur Rechnungskontrolle an die Buchhaltung übergeben"
                : `${data.count_booked} gebucht, ${data.count_created} neu angelegt`);
        } catch (e) {
            const det = e.response?.data?.detail;
            if (e.response?.status === 409 && det?.code === "possible_duplicate") {
                setDupWarn(det);
            } else {
                toast.error((typeof det === "string" && det) || det?.message || "Buchung fehlgeschlagen");
            }
        }
        setBusy(false);
    };

    const reset = () => { setScan(null); setRows([]); setResult(null); setScanError(""); setSupplierName(""); setDnNumber(""); setCommissionMode(false); setCommissionTarget(null); setDecisionRequired(false); setDecisionMade(false); setDocType("delivery_note"); setDupWarn(null); };

    if (loading) return <div className="min-h-screen bg-[#0E0E10] flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Lädt…</div>;
    if (error) return (
        <div className="min-h-screen bg-[#0E0E10] flex items-center justify-center px-4">
            <div className="bg-white rounded-sm p-6 max-w-sm text-center" data-testid="grscan-error">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                <p className="font-bold text-[#111827]">Nicht verfügbar</p>
                <p className="text-sm text-gray-600 mt-1">{error}</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50" data-testid="goods-receipt-scan-page">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment"
                onChange={onFile} className="hidden" data-testid="grscan-file-input" />

            <div className="bg-[#111827] text-white sticky top-0 z-30">
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-2">
                    <ScanLine className="w-5 h-5 text-[#FFCC00]" />
                    <div className="min-w-0">
                        <div className="font-display font-black text-lg leading-tight">Wareneingang erfassen</div>
                        <div className="text-[11px] text-gray-400 truncate">{info?.dealer_label}</div>
                    </div>
                    <span className="ml-auto text-xs bg-emerald-600 px-2 py-1 rounded-sm font-bold" data-testid="grscan-progress">
                        <Check className="w-3 h-3 inline mr-0.5" />{booked}
                    </span>
                </div>
                {info?.viewer_name && (
                    <div className="max-w-lg mx-auto px-4 pb-2 -mt-1 flex items-center gap-1.5 text-[11px] text-emerald-400" data-testid="grscan-viewer">
                        <UserCheck className="w-3.5 h-3.5" /> Angemeldet als {info.viewer_name}
                    </div>
                )}
            </div>

            <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
                {/* Aufnahme */}
                {!scan && !result && (
                    <div className="bg-white border border-gray-200 rounded-sm p-4 text-center" data-testid="grscan-capture-block">
                        <Package className="w-10 h-10 text-[#FFCC00] mx-auto mb-2" />
                        <p className="font-bold text-sm text-[#111827]">Lieferschein fotografieren oder PDF wählen</p>
                        <p className="text-[12px] text-gray-500 mt-1">Die KI erkennt Lieferant, Artikel, Menge und EK-Preise automatisch.</p>
                        <Button onClick={() => fileRef.current?.click()} disabled={scanning}
                            className="mt-4 w-full h-12 bg-[#111827] hover:bg-black text-white font-black uppercase tracking-wider" data-testid="grscan-capture-btn">
                            {scanning ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> KI analysiert…</> : <><Camera className="w-5 h-5 mr-2" /> Lieferschein aufnehmen</>}
                        </Button>
                        {scanning && (
                            <div className="mt-3 bg-purple-50 border-l-4 border-purple-500 px-3 py-2 text-left text-[12px] text-purple-900" data-testid="grscan-progress-hint">
                                <b>KI arbeitet…</b> Dokumenttyp erkennen · Lieferant &amp; Lieferschein-Nr. auslesen · Positionen mit Menge und EK-Preis extrahieren — das Ergebnis erscheint gleich hier zur Prüfung.
                            </div>
                        )}
                        {scanError && !scanning && (
                            <div className="mt-3 bg-red-50 border-l-4 border-red-500 px-3 py-2 text-left" data-testid="grscan-error-box">
                                <p className="text-sm font-bold text-red-800 flex items-center gap-1.5"><AlertCircle className="w-4 h-4 shrink-0" /> Scan nicht auswertbar</p>
                                <p className="text-[12px] text-red-800 mt-1">{scanError}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Positionen prüfen + buchen */}
                {scan && !result && (
                    <>
                        <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-2" data-testid="grscan-meta-block">
                            <div className="flex items-center gap-2 text-sm font-bold">
                                <Check className="w-4 h-4 text-green-600" /> Erkannte Daten
                                {scan.mocked && <span className="text-[10px] text-amber-700 font-normal">· Fallback (ohne KI)</span>}
                            </div>
                            {/* Jul-2026 (User-Wunsch) — Detail-Anzeige wie am PC: Belegtyp, Datum, Empfänger, KI-Sicherheit */}
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-gray-600 border-b border-gray-100 pb-2" data-testid="grscan-detail-grid">
                                <div>Belegtyp: <b className="text-[#111827]">{DOC_TYPE_DE[scan.doc_type] || "Lieferschein"}</b></div>
                                <div>Belegdatum: <b className="text-[#111827]">{scan.delivery_date || "—"}</b></div>
                                <div className="truncate">Empfänger: <b className="text-[#111827]">{scan.recipient_name || "—"}</b></div>
                                <div>KI-Sicherheit: <b className="text-[#111827]">{Math.round((scan.confidence || 0) * 100)} %</b></div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Lieferant</label>
                                <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Lieferant" className="h-10 mt-1" data-testid="grscan-supplier" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Lieferschein-Nr.</label>
                                <Input value={dnNumber} onChange={(e) => setDnNumber(e.target.value)} placeholder="LS-Nr." className="h-10 mt-1" data-testid="grscan-dn-number" />
                            </div>
                            {/* Aug-2026 (User-Wunsch) — Belegtyp wie am PC wählbar */}
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Belegtyp</label>
                                <select value={docType} onChange={(e) => setDocType(e.target.value)}
                                    className="mt-1 w-full h-10 border border-gray-300 rounded-sm px-2 text-sm bg-white" data-testid="grscan-doc-type">
                                    {DOC_TYPES.map((t) => <option key={t} value={t}>{DOC_TYPE_DE[t]}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Kommissionsware-Abfrage (Empfänger-Firmierung weicht ab / unklar) */}
                        <div className={`rounded-sm border p-3 ${decisionRequired && !decisionMade ? "bg-red-50 border-red-300" : commissionMode ? "bg-amber-50 border-amber-300" : "bg-white border-gray-200"}`} data-testid="grscan-commission-block">
                            {decisionRequired && !decisionMade ? (
                                <div data-testid="grscan-commission-decision">
                                    <p className="text-sm font-bold text-red-800 flex items-center gap-1.5">
                                        <AlertCircle className="w-4 h-4" /> Bitte entscheiden
                                    </p>
                                    <p className="text-[12px] text-red-800 mt-1">
                                        Empfänger: <b>„{scan.recipient_name || "nicht erkannt"}"</b>
                                        {scan.own_firmierung ? <> · Ihre Firmierung: <b>„{scan.own_firmierung}"</b></> : null}.
                                        {scan.recipient_match === "unknown" ? " Empfänger nicht sicher lesbar." : " Weicht von Ihrer Firmierung ab."}
                                    </p>
                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                        <Button type="button" onClick={() => { setCommissionMode(false); setDecisionMade(true); }}
                                            className="h-11 bg-[#111827] hover:bg-black text-white font-bold text-xs" data-testid="grscan-decision-own">
                                            Eigenes Lager
                                        </Button>
                                        <Button type="button" onClick={() => { setCommissionMode(true); setDecisionMade(true); }}
                                            className="h-11 bg-amber-500 hover:bg-amber-600 text-[#111827] font-bold text-xs" data-testid="grscan-decision-commission">
                                            Kommissionsware
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={commissionMode} onChange={(e) => { setCommissionMode(e.target.checked); setDecisionMade(true); }} className="w-4 h-4" data-testid="grscan-commission-toggle" />
                                    <span className="font-bold text-xs flex items-center gap-1.5">
                                        <AlertCircle className={`w-4 h-4 ${commissionMode ? "text-amber-600" : "text-gray-400"}`} />
                                        Kommissionsware
                                    </span>
                                </label>
                            )}
                            {commissionMode && (scan.commission_suggestions || []).length > 0 && (
                                <select
                                    value={commissionTarget ? `${commissionTarget.type}:${commissionTarget.id}` : ""}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setCommissionTarget(v ? (scan.commission_suggestions || []).find((s) => `${s.type}:${s.id}` === v) || null : null);
                                    }}
                                    className="mt-2 w-full border border-gray-300 rounded-sm px-2 h-10 text-sm bg-white" data-testid="grscan-commission-target">
                                    {(scan.commission_suggestions || []).map((s) => (
                                        <option key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>{s.name}</option>
                                    ))}
                                    <option value="">→ Konsignationsverwaltung (kein Ziel)</option>
                                </select>
                            )}
                        </div>

                        {docType === "control_only" ? (
                            <div className="bg-blue-50 border-l-4 border-blue-500 px-3 py-2.5 text-[12px] text-blue-900 rounded-sm" data-testid="grscan-control-only-hint">
                                <b>Keine Lagerbuchung.</b> Der Beleg wird nur zur Rechnungskontrolle an die Buchhaltung übergeben (Kreditoren).
                            </div>
                        ) : (
                        <div className="space-y-2" data-testid="grscan-rows">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-600 px-1">{rows.length} Position(en)</div>
                            {rows.map((r) => (
                                <div key={r.idx} className={`bg-white border rounded-sm p-3 ${r.action === "skip" ? "opacity-50 border-gray-200" : "border-gray-200"}`} data-testid={`grscan-row-${r.idx}`}>
                                    <Input value={r.name} onChange={(e) => updateRow(r.idx, { name: e.target.value })} placeholder="Bezeichnung" className="h-10 font-bold text-sm" data-testid={`grscan-name-${r.idx}`} />
                                    <div className="flex items-center gap-2 mt-2">
                                        <div className="flex-1">
                                            <label className="text-[9px] uppercase text-gray-500">Menge</label>
                                            <Input type="number" inputMode="decimal" min="0" step="0.01" value={r.quantity} onChange={(e) => updateRow(r.idx, { quantity: e.target.value })} className="h-10 text-right font-mono-num" data-testid={`grscan-qty-${r.idx}`} />
                                        </div>
                                        <div className="flex-1">
                                            <label className="text-[9px] uppercase text-gray-500">EK-€ netto</label>
                                            <Input type="number" inputMode="decimal" min="0" step="0.01" value={r.purchase_price_net} onChange={(e) => updateRow(r.idx, { purchase_price_net: e.target.value })} className="h-10 text-right font-mono-num" data-testid={`grscan-price-${r.idx}`} />
                                        </div>
                                        <div className="w-24">
                                            <label className="text-[9px] uppercase text-gray-500">Aktion</label>
                                            <select value={r.action} onChange={(e) => updateRow(r.idx, { action: e.target.value })}
                                                className="h-10 w-full border border-gray-300 rounded-sm text-xs bg-white" data-testid={`grscan-action-${r.idx}`}>
                                                <option value="match" disabled={!r.article_id}>Match</option>
                                                <option value="create">Neu</option>
                                                <option value="skip">Skip</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mt-1.5 text-[11px] flex items-center justify-between gap-2">
                                        {r._matchInfo ? (
                                            <span className="inline-flex items-center gap-1 bg-green-50 text-green-900 px-2 py-0.5 rounded-sm font-bold"><Check className="w-3 h-3" /> {r._matchInfo.score}% · {r._matchInfo.sku}</span>
                                        ) : (
                                            <span className="text-amber-700 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> wird neu angelegt</span>
                                        )}
                                        <span className="font-mono-num text-gray-500 shrink-0">{r.unit} · {fmtEur((Number(r.quantity) || 0) * (Number(r.purchase_price_net) || 0))}</span>
                                    </div>
                                </div>
                            ))}
                            {/* Summe der zu buchenden Positionen (Detail-Anzeige wie am PC) */}
                            <div className="bg-[#111827] text-white rounded-sm p-3 flex items-center justify-between text-sm" data-testid="grscan-sum-row">
                                <span className="font-bold">Zu buchen: {rows.filter((r) => r.action !== "skip" && Number(r.quantity) > 0).length} Position(en)</span>
                                <span className="font-mono-num font-black">
                                    {fmtEur(rows.filter((r) => r.action !== "skip").reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.purchase_price_net) || 0), 0))} netto
                                </span>
                            </div>
                        </div>
                        )}

                        {/* Aug-2026 (User-Report) — Dubletten-Warnung als klarer Dialog statt Roh-JSON */}
                        {dupWarn && (
                            <div className="bg-amber-50 border-2 border-amber-400 rounded-sm p-3" data-testid="grscan-dup-warning">
                                <p className="text-sm font-bold text-amber-900 flex items-center gap-1.5">
                                    <AlertCircle className="w-4 h-4 shrink-0" /> Möglicher Doppel-Scan
                                </p>
                                <p className="text-[12px] text-amber-900 mt-1">
                                    Für diesen Vorgang wurde bereits ein Beleg erfasst:
                                    <b> {dupWarn.duplicate?.supplier_name || "—"}</b>
                                    {dupWarn.duplicate?.delivery_note_number ? <> · Nr. <b>{dupWarn.duplicate.delivery_note_number}</b></> : null}
                                    {dupWarn.duplicate?.created_at ? <> · am {new Date(dupWarn.duplicate.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</> : null}
                                    {dupWarn.duplicate?.scanned_by_site ? <> · Standort: {dupWarn.duplicate.scanned_by_site}</> : null}.
                                    {" "}Erneutes Buchen würde den Bestand doppelt erhöhen.
                                </p>
                                <div className="grid grid-cols-2 gap-2 mt-2.5">
                                    <Button type="button" variant="outline" onClick={() => setDupWarn(null)}
                                        className="h-11 border-2 font-bold text-xs" data-testid="grscan-dup-cancel">
                                        Abbrechen
                                    </Button>
                                    <Button type="button" onClick={() => { setDupWarn(null); commit(true); }}
                                        className="h-11 bg-amber-500 hover:bg-amber-600 text-[#111827] font-bold text-xs" data-testid="grscan-dup-force">
                                        Trotzdem buchen
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className="sticky bottom-0 bg-gray-50 pt-2 pb-3 -mx-4 px-4 border-t border-gray-200 flex gap-2">
                            <Button onClick={reset} variant="outline" className="h-12 border-2" data-testid="grscan-reset">
                                <RotateCcw className="w-4 h-4" />
                            </Button>
                            <Button onClick={() => commit(false)} disabled={busy || (decisionRequired && !decisionMade)} className="flex-1 h-12 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider disabled:opacity-50" data-testid="grscan-commit">
                                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5 mr-1" /> {decisionRequired && !decisionMade ? "Erst wählen" : docType === "control_only" ? "Zur Rechnungskontrolle" : commissionMode ? "Als Kommission" : "Ins Lager buchen"}</>}
                            </Button>
                        </div>
                    </>
                )}

                {/* Ergebnis */}
                {result && (
                    <div className="bg-white border border-green-200 rounded-sm p-4 text-center" data-testid="grscan-result-block">
                        <Check className="w-10 h-10 text-green-600 mx-auto mb-2" />
                        <p className="font-black text-lg text-[#111827]">{result.doc_type === "control_only" ? "Übergeben!" : "Gebucht!"}</p>
                        <p className="text-sm text-gray-600 mt-1">{result.doc_type === "control_only"
                            ? "Beleg liegt zur Rechnungskontrolle in der Buchhaltung (Kreditoren)."
                            : <>{result.count_booked} Bestand erhöht · {result.count_created} neu angelegt{result.count_errors > 0 ? ` · ${result.count_errors} Fehler` : ""}</>}</p>
                        {[...(result.booked || []), ...(result.created || [])].length > 0 && (
                            <ul className="mt-3 text-left border border-gray-100 rounded-sm divide-y">
                                {[...(result.booked || []), ...(result.created || [])].map((b, i) => (
                                    <li key={i} className="px-3 py-2 text-xs flex items-center justify-between">
                                        <span className="font-bold truncate">{b.name}</span>
                                        <span className="font-mono-num font-bold text-green-700 ml-2">+{b.qty_added} → {b.stock_after}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <Button onClick={reset} className="mt-4 w-full h-12 bg-[#111827] hover:bg-black text-white font-black uppercase tracking-wider" data-testid="grscan-next">
                            <Camera className="w-5 h-5 mr-2" /> Nächsten Lieferschein
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
